// Sync-protocol tables. Populated by the sync module; the FS module
// only writes to vfs_changes (via sync/changes.ts) on rm. The rest
// of these tables stay empty until the sync task is implemented.

export const SYNC_STATEMENTS = [
  `CREATE TABLE IF NOT EXISTS vfs_manifests (
    hash      BLOB    PRIMARY KEY,
    size      INTEGER NOT NULL,
    encoded   BLOB    NOT NULL,
    last_seen INTEGER NOT NULL DEFAULT 0
  )`,
  // `prior` is the fingerprint of the version the delete removed (see
  // sync/fingerprint.ts), or NULL when it was not recorded: rows from
  // before v9, and tombstones a directory rename writes in bulk. The
  // column is duplicated in the v8 -> v9 migration; keep both in step.
  `CREATE TABLE IF NOT EXISTS vfs_changes (
    id    INTEGER PRIMARY KEY AUTOINCREMENT,
    rev   INTEGER NOT NULL,
    path  TEXT    NOT NULL,
    op    TEXT    NOT NULL CHECK(op IN ('delete')),
    prior TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS vfs_changes_by_rev ON vfs_changes(rev)`,
  // changes.ts looks up the latest op for a path via
  // `WHERE path = ? ORDER BY id DESC LIMIT 1`. Without the index
  // SQLite falls back to a full scan; with (path, id DESC) the
  // lookup is O(log n) and the ORDER BY drains straight from the
  // index. Used on every recordDelete and on every push-tick that
  // processes tombstones.
  `CREATE INDEX IF NOT EXISTS vfs_changes_by_path ON vfs_changes(path, id DESC)`,
  // coalesceChanges scans tombstones with
  // `WHERE rev > ? AND op = 'delete' GROUP BY path`. Neither of the
  // indexes above serves that: vfs_changes_by_rev(rev) can drive the
  // range but leaves GROUP BY path to a sort, so the planner instead
  // scans vfs_changes_by_path in path order and ignores the rev
  // predicate entirely — reading the whole table to return the handful
  // of rows in the watermark window.
  //
  // (op, rev) puts the equality column first and the range column
  // second, which is the shape SQLite can drive both halves from. The
  // GROUP BY still needs a temp b-tree, but the scan is now bounded by
  // the rev window instead of the table.
  //
  // This only started to matter once node_modules entered the sync set:
  // every reinstall appends thousands of tombstones and the table only
  // grows. Added at schema v8; `schema/migrations.ts` owns the upgrade.
  `CREATE INDEX IF NOT EXISTS vfs_changes_by_op_rev ON vfs_changes(op, rev)`,
  // Watermarks are keyed by (k, backend) so a workspace hosting
  // multiple backends keeps each backend's sync cursors
  // independent. The `backend` column was added at schema v3;
  // `schema/migrations.ts` owns the ALTER for existing
  // databases. Fresh installs land the composite key directly.
  `CREATE TABLE IF NOT EXISTS _vfs_watermark (
    k       TEXT    NOT NULL,
    backend TEXT    NOT NULL DEFAULT 'default',
    v       INTEGER NOT NULL,
    PRIMARY KEY (k, backend)
  )`,
  // The fetch cursor's same-rev `path` component, keyed by
  // (k, backend) so each backend resumes a partially-drained rev
  // independently. The rev component lives in _vfs_watermark under
  // 'fetchRev'; this table only holds the in-rev path. `backend`
  // mirrors _vfs_watermark and defaults to 'default'.
  `CREATE TABLE IF NOT EXISTS _vfs_fetch_cursor (
    k       TEXT    NOT NULL CHECK(k = 'fetch'),
    backend TEXT    NOT NULL DEFAULT 'default',
    path    TEXT,
    PRIMARY KEY (k, backend)
  )`,
  `CREATE TABLE IF NOT EXISTS _vfs_push_cursor (
    k       TEXT    NOT NULL CHECK(k = 'push'),
    backend TEXT    NOT NULL DEFAULT 'default',
    rev     INTEGER NOT NULL DEFAULT 0,
    path    TEXT,
    PRIMARY KEY (k, backend)
  )`,
  // Durable half of a restartable sync operation. One row per
  // (backend, direction): the plan's key. A restarted iterator reads
  // this row to recover the fixed target and generation it was working
  // against, because a JavaScript generator cannot survive eviction.
  //
  // `target_rev` / `target_path` are NULL only while status is
  // 'capturing' — pull cannot capture its target atomically because
  // watermarks({settle:true}) is a remote call, so capture is a
  // two-phase insert-then-promote. `block_after_*` records where an
  // in-flight block began and is cleared once the cursor advances; a
  // later invocation that finds the marker with no cursor progress
  // knows the prior execution was interrupted.
  //
  // The committed progress cursor stays in _vfs_watermark. This table
  // never duplicates it.
  `CREATE TABLE IF NOT EXISTS _vfs_sync_operations (
    backend             TEXT    NOT NULL,
    direction           TEXT    NOT NULL CHECK (direction IN ('pull', 'push')),
    generation          TEXT    NOT NULL,
    status              TEXT    NOT NULL CHECK (
                          status IN ('capturing', 'pending', 'failed', 'lost')
                        ),
    target_rev          INTEGER,
    target_path         TEXT,
    runtime_id          TEXT,
    mode                TEXT    CHECK (mode IN ('entries', 'pack')),
    block_after_rev     INTEGER,
    block_after_path    TEXT,
    block_started_at    INTEGER,
    internal_max_entries INTEGER NOT NULL,
    internal_max_bytes  INTEGER NOT NULL,
    created_at          INTEGER NOT NULL,
    updated_at          INTEGER NOT NULL,
    last_error          TEXT,
    PRIMARY KEY (backend, direction)
  )`,
  // Entries the receiver deliberately refused, most often a
  // container-side write under a read-only mount. The cursor advances
  // past a rejection so the operation cannot stall on it forever,
  // which means the only record of the drop would otherwise be a
  // yielded progress value the caller may have discarded. These rows
  // outlive the operation row and are pruned when a new generation
  // starts. See docs/decisions/sync-operations.md.
  `CREATE TABLE IF NOT EXISTS _vfs_sync_skips (
    backend    TEXT    NOT NULL,
    direction  TEXT    NOT NULL CHECK (direction IN ('pull', 'push')),
    generation TEXT    NOT NULL,
    path       TEXT    NOT NULL,
    reason     TEXT    NOT NULL,
    at         INTEGER NOT NULL,
    PRIMARY KEY (backend, direction, generation, path)
  )`,
  // The `mode` column was added at schema v2 and `version` at v10;
  // `schema/migrations.ts` owns the ALTERs for existing databases. Keep the CHECK
  // constraint here aligned with the migration's CHECK so fresh
  // installs and upgrades enforce the same allowed set.
  `CREATE TABLE IF NOT EXISTS _vfs_mounts (
    root    TEXT PRIMARY KEY,
    kind    TEXT NOT NULL,
    indexed INTEGER NOT NULL DEFAULT 0,
    mode    TEXT NOT NULL DEFAULT 'read-only'
            CHECK(mode IN ('read-only', 'read-write')),
    version TEXT
  )`,
] as const;
